/**
 * Conta do PDF comparativo: consórcio × financiamento × aplicação.
 *
 * Tudo aqui é função pura — as taxas entram prontas (ver indicadoresBcb.ts) e o
 * PDF (comparativoPdf.ts) só desenha o que sai daqui. Os valores são SEM seguro
 * dos dois lados: o seguro do consórcio e o MIP/DFI do financiamento existem,
 * mas não são o que está sendo comparado.
 *
 * ── 1. CONSÓRCIO × FINANCIAMENTO ────────────────────────────────────────────
 * Mesmo crédito, mesmo prazo. O custo do consórcio é a taxa de administração
 * (crédito × taxa); o do financiamento são os juros, à taxa MÉDIA de mercado do
 * BCB. Veículo em Price (parcela fixa, como os bancos fazem), imóvel em SAC.
 * Os dois sistemas saem sempre calculados (sac, price): a tela do simulador
 * mostra os dois lado a lado; o PDF usa o do segmento como principal.
 * A taxa do BCB é efetiva ao ano, então a mensal é (1+a)^(1/12) − 1, e não a/12.
 *
 * ── 2. VENDER A CARTA CONTEMPLADA × APLICAR O MESMO DINHEIRO ────────────────
 * O cliente paga a parcela até contemplar no mês m e vende a carta: recebe de
 * volta o que pagou mais um ágio sobre o crédito. O ágio é ganho de capital
 * (15% de IR). A alternativa é pôr as MESMAS parcelas, nos mesmos meses, num
 * CDB a 100% do CDI, com o IR regressivo de cada aporte conforme o tempo que
 * ficou aplicado. Crédito e parcela do consórcio sobem uma vez por ano pelo
 * índice do segmento (INCC no imóvel, IPCA no veículo como aproximação da
 * tabela de preços).
 *
 * Ágio de carta contemplada não tem índice oficial — é negociação. Por isso o
 * PDF mostra também o ÁGIO DE EMPATE: o mínimo para a carta render igual ao
 * CDB. Esse número não depende de premissa nenhuma além das taxas.
 */

export type Segmento = 'imovel' | 'veiculo';

export type EntradaComparativo = {
  credito: number;
  prazo: number;
  /** Taxa de administração TOTAL do plano, em % (ex.: 25.5). */
  taxaAdminPct: number;
  /** Parcela mensal SEM seguro antes da contemplação (já com flex, se houver). */
  parcelaPre: number;
  mesContemplacao: number;
  segmento: Segmento;
};

export type Taxas = {
  /** Juros médios do financiamento do segmento, % a.a. efetiva. */
  jurosFinanciamentoAa: number;
  cdiAa: number;
  /** Reajuste anual de crédito e parcela, %. */
  reajusteAa: number;
  /** Ágio da venda da carta, % do crédito já reajustado. */
  agioPct: number;
};

export const IR_GANHO_CAPITAL = 0.15;

/** Tabela regressiva de renda fixa, por dias aplicados. */
export const aliquotaIrRendaFixa = (dias: number) =>
  dias <= 180 ? 0.225 : dias <= 360 ? 0.2 : dias <= 720 ? 0.175 : 0.15;

export const mensalDeAnual = (aaPct: number) => Math.pow(1 + aaPct / 100, 1 / 12) - 1;
export const anualDeMensal = (am: number) => (Math.pow(1 + am, 12) - 1) * 100;

// ── 1 ─────────────────────────────────────────────────────────────────────────

export type Financiamento = {
  sistema: 'Price' | 'SAC';
  taxaAa: number;
  taxaAm: number;
  parcelaInicial: number;
  parcelaFinal: number;
  totalPago: number;
  juros: number;
};

export const calcularFinanciamento = (
  valor: number,
  prazo: number,
  taxaAa: number,
  sistema: 'Price' | 'SAC',
): Financiamento => {
  const i = mensalDeAnual(taxaAa);
  if (sistema === 'Price') {
    const parcela = i > 0 ? (valor * i) / (1 - Math.pow(1 + i, -prazo)) : valor / prazo;
    const totalPago = parcela * prazo;
    return { sistema, taxaAa, taxaAm: i * 100, parcelaInicial: parcela, parcelaFinal: parcela, totalPago, juros: totalPago - valor };
  }
  const amortizacao = valor / prazo;
  // Juros sobre saldo decrescente: i·A·(n + (n−1) + … + 1) = i·A·n(n+1)/2.
  const juros = i * amortizacao * (prazo * (prazo + 1)) / 2;
  return {
    sistema,
    taxaAa,
    taxaAm: i * 100,
    parcelaInicial: amortizacao + valor * i,
    parcelaFinal: amortizacao * (1 + i),
    totalPago: valor + juros,
    juros,
  };
};

/**
 * Juros pagos acumulados até cada mês (índice 0 = mês 1) — a curva do gráfico
 * "custo ao longo do plano". O último ponto é o `juros` de calcularFinanciamento.
 */
export const jurosAcumulados = (valor: number, prazo: number, taxaAa: number, sistema: 'Price' | 'SAC'): number[] => {
  const i = mensalDeAnual(taxaAa);
  const parcelaPrice = i > 0 ? (valor * i) / (1 - Math.pow(1 + i, -prazo)) : valor / prazo;
  const serie: number[] = [];
  let saldo = valor;
  let acumulado = 0;
  for (let k = 1; k <= prazo; k++) {
    const juros = saldo * i;
    acumulado += juros;
    saldo -= sistema === 'SAC' ? valor / prazo : parcelaPrice - juros;
    serie.push(acumulado);
  }
  return serie;
};

export type ConsorcioXFinanciamento = {
  consorcioTotal: number;
  consorcioCusto: number;
  /** O sistema do segmento (SAC no imóvel, Price no veículo). */
  financiamento: Financiamento;
  sac: Financiamento;
  price: Financiamento;
  economia: number;
  /** Economia em % do que o financiamento custaria em juros. */
  economiaPctDosJuros: number;
};

export const compararFinanciamento = (e: EntradaComparativo, t: Taxas): ConsorcioXFinanciamento => {
  const consorcioCusto = e.credito * (e.taxaAdminPct / 100);
  const sac = calcularFinanciamento(e.credito, e.prazo, t.jurosFinanciamentoAa, 'SAC');
  const price = calcularFinanciamento(e.credito, e.prazo, t.jurosFinanciamentoAa, 'Price');
  const financiamento = e.segmento === 'imovel' ? sac : price;
  const economia = financiamento.juros - consorcioCusto;
  return {
    consorcioTotal: e.credito + consorcioCusto,
    consorcioCusto,
    financiamento,
    sac,
    price,
    economia,
    economiaPctDosJuros: financiamento.juros > 0 ? (economia / financiamento.juros) * 100 : 0,
  };
};

// ── 2 ─────────────────────────────────────────────────────────────────────────

export type CenarioVenda = {
  mes: number;
  totalPago: number;
  creditoReajustado: number;
  agioBruto: number;
  recebidoLiquido: number;
  lucroCarta: number;
  rentabilidadeCartaAa: number;
  cdbLiquido: number;
  lucroCdb: number;
  rentabilidadeCdbAa: number;
  /** Ágio mínimo (% do crédito reajustado) para a carta empatar com o CDB. */
  agioEmpatePct: number;
  cartaGanha: boolean;
};

/** TIR mensal de fluxos em t = 0..n (bisseção; os fluxos daqui têm uma troca de sinal só). */
export const tirMensal = (fluxos: number[]): number => {
  const vpl = (r: number) => fluxos.reduce((acc, f, t) => acc + f / Math.pow(1 + r, t), 0);
  let lo = -0.99;
  let hi = 1;
  if (vpl(lo) * vpl(hi) > 0) return Number.NaN;
  for (let k = 0; k < 200; k++) {
    const mid = (lo + hi) / 2;
    if (vpl(lo) * vpl(mid) <= 0) hi = mid;
    else lo = mid;
  }
  return (lo + hi) / 2;
};

/**
 * Parcela k (1..m) é paga no início do mês, em t = k−1; a carta é vendida em
 * t = m. O reajuste anual acontece a cada 12 parcelas: fator^⌊(k−1)/12⌋.
 */
/** Os valores da venda no mês, sem a TIR — barato o bastante para a curva mês a mês do gráfico. */
export const valoresDaVenda = (e: EntradaComparativo, t: Taxas, mes: number) => {
  const fatorAno = 1 + t.reajusteAa / 100;
  const cdiAm = mensalDeAnual(t.cdiAa);
  let totalPago = 0;
  let cdbLiquido = 0;
  for (let k = 1; k <= mes; k++) {
    const p = e.parcelaPre * Math.pow(fatorAno, Math.floor((k - 1) / 12));
    totalPago += p;
    const meses = mes - (k - 1);
    const bruto = p * Math.pow(1 + cdiAm, meses);
    cdbLiquido += bruto - (bruto - p) * aliquotaIrRendaFixa(meses * 30);
  }
  const creditoReajustado = e.credito * Math.pow(fatorAno, Math.floor((mes - 1) / 12));
  const recebidoLiquido = totalPago + creditoReajustado * (t.agioPct / 100) * (1 - IR_GANHO_CAPITAL);
  return { totalPago, cdbLiquido, recebidoLiquido };
};

export const simularVendaDaCarta = (e: EntradaComparativo, t: Taxas, mes: number): CenarioVenda => {
  const fatorAno = 1 + t.reajusteAa / 100;
  const cdiAm = mensalDeAnual(t.cdiAa);

  const parcelas: number[] = [];
  for (let k = 1; k <= mes; k++) parcelas.push(e.parcelaPre * Math.pow(fatorAno, Math.floor((k - 1) / 12)));
  const totalPago = parcelas.reduce((a, b) => a + b, 0);

  const creditoReajustado = e.credito * Math.pow(fatorAno, Math.floor((mes - 1) / 12));
  const agioBruto = creditoReajustado * (t.agioPct / 100);
  const recebidoLiquido = totalPago + agioBruto * (1 - IR_GANHO_CAPITAL);

  let cdbLiquido = 0;
  parcelas.forEach((p, idx) => {
    const meses = mes - idx; // aporte em t = idx, resgate em t = mes
    const bruto = p * Math.pow(1 + cdiAm, meses);
    cdbLiquido += bruto - (bruto - p) * aliquotaIrRendaFixa(meses * 30);
  });

  const fluxo = (final: number) => {
    const f = new Array<number>(mes + 1).fill(0);
    parcelas.forEach((p, idx) => { f[idx] -= p; });
    f[mes] += final;
    return f;
  };
  const rentabilidadeCartaAa = anualDeMensal(tirMensal(fluxo(recebidoLiquido)));
  const rentabilidadeCdbAa = anualDeMensal(tirMensal(fluxo(cdbLiquido)));

  const agioEmpatePct = creditoReajustado > 0
    ? Math.max(0, ((cdbLiquido - totalPago) / (1 - IR_GANHO_CAPITAL) / creditoReajustado) * 100)
    : 0;

  return {
    mes,
    totalPago,
    creditoReajustado,
    agioBruto,
    recebidoLiquido,
    lucroCarta: recebidoLiquido - totalPago,
    rentabilidadeCartaAa,
    cdbLiquido,
    lucroCdb: cdbLiquido - totalPago,
    rentabilidadeCdbAa,
    agioEmpatePct,
    cartaGanha: recebidoLiquido > cdbLiquido,
  };
};

/**
 * Meses da tabela de sensibilidade: marcos fixos até o prazo, mais o mês da
 * proposta (se não for um deles). Sempre ordenados e sem repetição.
 */
export const mesesDaTabela = (prazo: number, mesProposta: number): number[] => {
  const marcos = [6, 12, 24, 36, 48, 60, 84, 120].filter((m) => m < prazo);
  const todos = new Set([...marcos, Math.min(Math.max(1, mesProposta), prazo)]);
  return [...todos].sort((a, b) => a - b);
};

export type Comparativo = {
  entrada: EntradaComparativo;
  taxas: Taxas;
  financiamento: ConsorcioXFinanciamento;
  cenarioProposta: CenarioVenda;
  tabela: CenarioVenda[];
  /** Último mês da tabela em que vender a carta ainda rende mais que o CDB (null se nenhum). */
  ultimoMesQueGanha: number | null;
};

export const montarComparativo = (e: EntradaComparativo, t: Taxas): Comparativo => {
  const tabela = mesesDaTabela(e.prazo, e.mesContemplacao).map((m) => simularVendaDaCarta(e, t, m));
  const ganhadores = tabela.filter((c) => c.cartaGanha);
  return {
    entrada: e,
    taxas: t,
    financiamento: compararFinanciamento(e, t),
    cenarioProposta: simularVendaDaCarta(e, t, Math.min(Math.max(1, e.mesContemplacao), e.prazo)),
    tabela,
    ultimoMesQueGanha: ganhadores.length ? ganhadores[ganhadores.length - 1].mes : null,
  };
};
